import React, { useEffect, useState } from "react";
import { useNavigation } from "../../../hooks";
import { termsService } from "../../../services";
import { TermsListItem } from "../../../types/terms";
import LoadingSpinner from "../../common/LoadingSpinner";
import Card from "../../ui/Card";
import Icon from "../../ui/Icon";
import Modal from "../../ui/Modal";

const MyDataTermsListPage: React.FC = () => {
  const { goBack } = useNavigation();
  const [terms, setTerms] = useState<TermsListItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selectedTerm, setSelectedTerm] = useState<TermsListItem | null>(null);
  const [isModalOpen, setIsModalOpen] = useState(false);

  useEffect(() => {
    const fetchTerms = async () => {
      try {
        setLoading(true);
        const termsData = await termsService.getTermsList();
        setTerms(termsData);
      } catch (err) {
        console.error("약관 목록 조회 실패:", err);
        setError("약관 목록을 불러오는데 실패했습니다.");
      } finally {
        setLoading(false);
      }
    };

    fetchTerms();
  }, []);

  // 약관을 카테고리별로 분류
  // agreementSystemDivisionCode가 30, 31, 32인 항목이 공공기관 약관
  const publicInstitutionTerms = terms.filter(
    (term) =>
      term.agreementSystemDivisionCode === "30" ||
      term.agreementSystemDivisionCode === "31" ||
      term.agreementSystemDivisionCode === "32"
  );

  // 공공기관이 아닌 나머지가 의료마이데이터 약관
  const medicalTerms = terms.filter(
    (term) =>
      term.agreementSystemDivisionCode !== "30" &&
      term.agreementSystemDivisionCode !== "31" &&
      term.agreementSystemDivisionCode !== "32"
  );

  const handleTermClick = (term: TermsListItem) => {
    setSelectedTerm(term);
    setIsModalOpen(true);
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
    setSelectedTerm(null);
  };

  if (loading) {
    return (
      <div className="h-screen-dvh bg-white flex flex-col">
        <header className="bg-white px-4 py-3 flex items-center">
          <button
            onClick={goBack}
            className="p-2 hover:bg-gray-100 rounded-full transition-colors"
          >
            <Icon name="chevron-left" className="text-gray-600" />
          </button>        </header>
        <div className="flex-1 flex items-center justify-center">
          <LoadingSpinner />
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="h-screen-dvh bg-white flex flex-col">
        <header className="bg-white px-4 py-3 flex items-center">
          <button
            onClick={goBack}
            className="p-2 hover:bg-gray-100 rounded-full transition-colors"
          >
            <Icon name="chevron-left" className="text-gray-600" />
          </button>        </header>
        <div className="flex-1 flex items-center justify-center">
          <p className="text-red-500">{error}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="h-screen-dvh bg-white flex flex-col">
      {/* 상단 헤더 */}
      <header className="bg-white px-4 py-3 flex items-center">
        <button
          onClick={goBack}
          className="p-2 hover:bg-gray-100 rounded-full transition-colors"
        >
          <Icon name="chevron-left" className="text-gray-600" />
        </button>
        <h1 className="text-lg font-semibold text-gray-800">
          마이데이터 약관 및 이용동의
        </h1>
      </header>

      {/* 메인 콘텐츠 */}
      <main className="flex-1 overflow-y-auto">
        <div className="p-4 space-y-6">
          {/* 의료마이데이터 필수 이용 동의 섹션 */}
          {medicalTerms.length > 0 && (
            <section>
              <h2 className="text-base font-semibold text-gray-900 mb-3">
                의료마이데이터 필수 이용 동의
              </h2>
              <div className="space-y-2">
                {medicalTerms.map((term) => (
                  <Card
                    key={term.termsNo}
                    className="cursor-pointer hover:bg-gray-50 transition-colors"
                  >
                    <button
                      onClick={() => handleTermClick(term)}
                      className="w-full text-left flex items-center justify-between py-2"
                    >
                      <span className="text-gray-800 text-sm">
                        {term.termsName}
                      </span>
                      <Icon name="chevron-right" className="text-gray-400" />
                    </button>
                  </Card>
                ))}
              </div>
            </section>
          )}

          {/* 공공기관 이용 동의 섹션 */}
          {publicInstitutionTerms.length > 0 && (
            <section>
              <h2 className="text-base font-semibold text-gray-900 mb-3">
                공공기관 이용 동의
              </h2>
              <div className="space-y-2">
                {publicInstitutionTerms.map((term) => (
                  <Card
                    key={term.termsNo}
                    className="cursor-pointer hover:bg-gray-50 transition-colors"
                  >
                    <button
                      onClick={() => handleTermClick(term)}
                      className="w-full text-left flex items-center justify-between py-2"
                    >
                      <span className="text-gray-800 text-sm">
                        {term.termsName}
                      </span>
                      <Icon name="chevron-right" className="text-gray-400" />
                    </button>
                  </Card>
                ))}
              </div>
            </section>
          )}

          {/* 약관이 없을 경우 */}
          {terms.length === 0 && (
            <div className="text-center py-12">
              <p className="text-gray-500">등록된 약관이 없습니다.</p>
            </div>
          )}
        </div>
      </main>

      {/* 약관 상세 모달 */}
      <Modal
        isOpen={isModalOpen}
        onClose={handleCloseModal}
        title={selectedTerm?.termsName || ""}
      >
        <div
          className="text-sm text-gray-700 leading-relaxed"
          dangerouslySetInnerHTML={{
            __html: selectedTerm?.termsHtmlContents || "",
          }}
        />
      </Modal>
    </div>
  );
};

export default MyDataTermsListPage;
